import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface FearGreedHistoryData {
  id: string;
  date: string;
  value: number;
  classification: string;
  created_at: string;
}

export const useFearGreedHistory = (days: number = 30) => {
  return useQuery({
    queryKey: ['fng-history', days],
    queryFn: async (): Promise<FearGreedHistoryData[]> => {
      const daysAgo = new Date();
      daysAgo.setDate(daysAgo.getDate() - days);
      const dateString = daysAgo.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('fng_history')
        .select('*')
        .gte('date', dateString)
        .order('date', { ascending: false });

      if (error) {
        console.error('Error fetching Fear & Greed history:', error);
        throw new Error(`Failed to fetch Fear & Greed history: ${error.message}`);
      }

      return data || [];
    },
    staleTime: 60 * 60 * 1000, // 1 hour
    refetchInterval: 60 * 60 * 1000,
    enabled: true,
  });
};

export const useFearGreedLatest = () => {
  return useQuery({
    queryKey: ['fng-latest'],
    queryFn: async (): Promise<FearGreedHistoryData | null> => {
      // Time-bound the query to the last 30 days to allow the BRIN index to filter efficiently
      // instead of performing a full table seq-scan for the ORDER BY LIMIT.
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const dateString = thirtyDaysAgo.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('fng_history')
        .select('*')
        .gte('date', dateString)
        .order('date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching latest Fear & Greed:', error);
        throw new Error(`Failed to fetch latest Fear & Greed: ${error.message}`);
      }

      return data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    refetchInterval: false, // Adjusted since real-time will replace polling soon if needed
  });
};

export const getFearGreedColor = (value: number): string => {
  if (value <= 25) return 'hsl(var(--destructive))'; // Extreme Fear - Red
  if (value <= 45) return 'hsl(var(--warning))'; // Fear - Orange
  if (value <= 55) return 'hsl(var(--muted-foreground))'; // Neutral - Gray
  if (value <= 75) return 'hsl(var(--success))'; // Greed - Green
  return 'hsl(var(--primary))'; // Extreme Greed - Blue
};

export const getFearGreedLabel = (value: number): string => {
  if (value <= 25) return 'Extreme Fear';
  if (value <= 45) return 'Fear';
  if (value <= 55) return 'Neutral';
  if (value <= 75) return 'Greed';
  return 'Extreme Greed';
};